import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import BookNowModal from "../GuestLandingPage/BookNowModal";
import Skeleton from "../../Components/Skeleton/Skeleton";
import {
  useStatusQuery,
  useTilesDataQuery,
  useTodaysOrdersQuery,
  useOrdersByDateQuery,
} from "../../Store/Apis/bookingApi";
import {
  SearchOutlined,
  DownOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { Table } from "antd";
import getStatusColor from "../../Hooks/GetStatusColor";
import { useOutletsQuery } from "../../Store/Apis/outletApi";
import { isPermissionGranted } from "../../Hooks/permissionsMiddleware";
import formatDate from "../../Hooks/formatDate";
import { useSelector } from "react-redux";
import "./DashboardStyles.css"; // Import our new custom styles
Chart.register(CategoryScale);

const Dashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const [chartData, setChartData] = useState({
    labels: ["Pending", "Confirmed", "In Progress", "Completed", "Cancel"],
    datasets: [
      {
        data: [16, 10, 2, 10, 2],
        backgroundColor: [
          "#8392ab",
          "#11cdef",
          "#044E7B",
          "#2dce89",
          "#f5365c",
        ],
        borderWidth: 0,
      },
    ],
  });

  // New state for the bar chart
  const [barChartData, setBarChartData] = useState({
    labels: ["Pending", "Confirmed", "In Progress", "Completed", "Cancel"],
    datasets: [
      {
        label: "Orders by Status",
        data: [16, 10, 2, 10, 2],
        backgroundColor: [
          "#8392ab",
          "#11cdef",
          "#044E7B",
          "#2dce89",
          "#f5365c",
        ],
        borderRadius: 6,
      },
    ],
  });

  const statusList = useStatusQuery();
  const tilesData = useTilesDataQuery({
    outletID: user?.outletID,
  });
  const [sortFilter, setSortFilter] = useState({
    search: "",
    outlet: "",
    status: "",
    technicianID: "",
  });

  const todaysOrders = useTodaysOrdersQuery({
    keyword: sortFilter.search,
    statusName: sortFilter.status,
    technicianID: sortFilter.technicianID,
    outletID: user?.outletID,
  });

  // Define columns for the Ant Design Table
  const columns = [
    {
      title: "Order Info",
      dataIndex: "orderInfo",
      fixed: "left",
      width: 220,
      render: (_, record) => (
        <>
          <p className="text-sm mb-0">
            <span className="me-2 fw-medium">Order ID:</span>
            <span className="font-weight-bold">{record.orderNumber}</span>
          </p>
          <p className="text-sm mb-0">
            <span className="me-2 fw-medium">Order Date:</span>
            <span>{formatDate(record.orderDate)}</span>
          </p>
        </>
      ),
    },
    {
      title: "Outlet",
      dataIndex: "outletName",
    },
    {
      title: "Customer",
      dataIndex: "customer",
      render: (_, record) => (
        <>
          <p className="text-sm font-weight-bold mb-0">{record.customerName}</p>
          <p className="text-sm mb-0">{record.customerNumber}</p>
        </>
      ),
    },
    {
      title: "Order Amount",
      dataIndex: "subTotal",
      render: (subTotal) => `R ${subTotal}`,
    },
    {
      title: "Status",
      dataIndex: "statusDescription",
      render: (status) => (
        <span
          className={`badge badge-${getStatusColor(status)}`}
          style={{ cursor: "default" }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Payment",
      dataIndex: "payment",
      render: (_, record) => (
        <>
          <p className="text-sm mb-0">
            <span className="me-2 fw-medium">Total:</span>
            <span className="font-weight-bold">R {record.subTotal}</span>
          </p>
          <p className="text-sm mb-0">
            <span className="me-2 fw-medium">Paid:</span>
            <span className="font-weight-bold">R {record.amountPaid}</span>
          </p>
        </>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      render: (_, record) => (
        <p className="text-sm mb-0">{record.customerName}</p>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <>
          {isPermissionGranted(2) && (
            <Link
              to={`/sales-history/details?id=${record.orderId}`}
              className="badge badge-primary text-xs fw-600 mx-1"
            >
              View
            </Link>
          )}
          {isPermissionGranted(1) &&
            record.statusDescription !== "Completed" &&
            (() => {
              // Internal booking: only creator can edit
              if (record.createdBy.toString() !== "0") {
                return record.createdBy.toString() === user.id.toString();
              }
              // External booking (createdBy === 0): only reservationist role can edit
              const userRoleName = String(
                user?.role || user?.roleName || ""
              ).toLowerCase();
              return userRoleName === "reservationist";
            })() && (
              <Link
                to={`/sales-history/edit?id=${record.orderId}`}
                className="badge badge-warning text-xs fw-600 mx-1"
              >
                Edit
              </Link>
            )}
        </>
      ),
    },
  ];

  // Transform data to fit the Ant Design table structure
  const dataSource = todaysOrders?.data?.data?.map((elem, index) => ({
    key: index,
    orderNumber: elem.orderNumber,
    orderDate: elem.orderDate,
    outletName: elem.outletName,
    customerName: elem.customerName,
    customerNumber: elem.customerNumber,
    subTotal: Number(elem.subTotal + elem.taxAmount - elem.discount).toFixed(2),
    statusDescription: elem.statusDescription,
    amountPaid: Number(elem.amountPaid).toFixed(2),
    totalAmount: Number(elem.totalAmount).toFixed(2),
    orderId: elem.orderId,
    createdBy: elem.createdBy,
  }));

  function transformData(response) {
    const labels = [];
    const data = [];
    const backgroundColor = [];
    response.data.forEach((item) => {
      labels.push(item.title);
      data.push(item.value);

      // Convert color names to hex if necessary
      if (item.color.startsWith("bg-")) {
        const colorMap = {
          "bg-secondary": "#8392ab",
          "bg-primary": "#044E7B",
          "bg-info": "#11cdef",
          "bg-success": "#2dce89",
          "bg-danger": "#f5365c",
        };
        backgroundColor.push(colorMap[item.color] || "#cccccc");
      } else {
        backgroundColor.push(item.color); // Use custom color directly
      }
    });

    return {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: backgroundColor,
          borderWidth: 0,
        },
      ],
    };
  }

  function transformBarChartData(response) {
    const labels = [];
    const data = [];
    const backgroundColor = [];
    response.data.forEach((item) => {
      labels.push(item.title);
      data.push(item.value);

      // Convert color names to hex if necessary
      if (item.color.startsWith("bg-")) {
        const colorMap = {
          "bg-secondary": "#8392ab",
          "bg-primary": "#044E7B",
          "bg-info": "#11cdef",
          "bg-warning": "red",
          "bg-success": "#2dce89",
          "bg-danger": "#f5365c",
        };
        backgroundColor.push(colorMap[item.color] || "#cccccc");
      } else {
        backgroundColor.push(item.color); // Use custom color directly
      }
    });

    return {
      labels: labels,
      datasets: [
        {
          label: "Orders by Status",
          data: data,
          backgroundColor: backgroundColor,
          borderRadius: 6,
          barThickness: 18,
          maxBarThickness: 24,
        },
      ],
    };
  }

  useEffect(() => {
    if (tilesData.isSuccess) {
      const chartData2 = transformData(tilesData?.data);
      setChartData(chartData2);

      // Also update bar chart data
      const barData = transformBarChartData(tilesData?.data);
      setBarChartData(barData);
    }
  }, [tilesData.isSuccess]);

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setSortFilter({
        ...sortFilter,
        outlet: user.outletID,
        technicianID: user.role_Id == 31 ? user.id : "",
      });
    }
  }, []);

  // Chart options for better styling
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        cornerRadius: 6,
        displayColors: true,
        boxPadding: 6,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.04)",
        },
        ticks: {
          font: {
            size: 12,
          },
          maxTicksLimit: 6, // Limit number of ticks for better readability
          callback: function (value) {
            if (value >= 1000) {
              return value / 1000 + "k";
            }
            return value;
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          maxRotation: 0, // Prevent label rotation
          autoSkip: true, // Skip labels if they don't fit
          autoSkipPadding: 15, // Give labels more space
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        right: 15,
        bottom: 10,
        left: 15,
      },
    },
    // Make bar chart more responsive
    onResize: function (chart, size) {
      if (size.width < 400) {
        chart.options.scales.x.ticks.maxRotation = 45; // Allow rotation on small screens
      } else {
        chart.options.scales.x.ticks.maxRotation = 0;
      }
    },
  };

  // Add this near your other state declarations
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)), // Last 7 days
    endDate: new Date(),
  });

  // Replace or add this with your existing hooks
  const ordersByDate = useOrdersByDateQuery({
    outletId: user?.outletID || "",
    // startDate: dateRange.startDate.toISOString(),
    // endDate: dateRange.endDate.toISOString(),
  });

  // Modify the getPercentChange function to use actual data
  const getPercentChange = (statusTitle) => {
    if (!ordersByDate.data?.data) return 0;
    const statusData = ordersByDate.data.data.find(
      (item) => item.status_description === statusTitle
    );
    return statusData?.percentage_increase ?? 0;
  };

  const [showBookNowModal, setShowBookNowModal] = useState(false);

  const handleBookNow = () => {
    setShowBookNowModal(true); // Opens the BookNowModal
  };

  return (
    <div className="dashboard-container">
      <div className="row mb-4">
        <div className="col-12">
          {/* Change mb-4 to dashboard-title to use our scoped styles */}
          <h4 className="dashboard-title">Dashboard Overview</h4>
          <div className="tile-container">
            {tilesData.isLoading &&
              [1, 2, 3, 4, 5, 6, 7].map((elem, i) => (
                <div className="tile-wrapper" key={i}>
                  <Skeleton
                    type={"rounded"}
                    height="165px"
                    width="100%"
                    backgroundColor="rgb(255 255 255 / 20%)"
                    className="mb-0"
                  />
                </div>
              ))}
            {tilesData.isSuccess &&
              tilesData.data.data.map((elem, i) => {
                const percentChange = getPercentChange(elem.title);
                const isPositive = parseFloat(percentChange) > 0;

                return (
                  <div className="tile-wrapper" key={i}>
                    <Link
                      to={`/sales-history?status=${encodeURIComponent(
                        elem.title
                      )}`}
                      className="dashboard-tile"
                    >
                      <div className="card-body">
                        <div
                          className="tile-icon"
                          datatype="revenue"
                          style={{
                            backgroundColor: elem.color.startsWith("bg-")
                              ? `var(--${elem.color.substring(
                                  3
                                )}-light, #f0f9ff)`
                              : "#f0f9ff",
                          }}
                        >
                          <i
                            className={`${elem.icon}`}
                            style={{
                              color: elem.color.startsWith("bg-")
                                ? `var(--${elem.color.substring(3)}, #044E7B)`
                                : "#fff",
                            }}
                            aria-hidden="true"
                          />
                        </div>
                        <h6 className="tile-title">{elem.title}</h6>
                        <h3 className="tile-value">
                          {(() => {
                            if (
                              ordersByDate.isSuccess &&
                              ordersByDate.data?.data
                            ) {
                              const statusData = ordersByDate.data.data.find(
                                (item) => item.status_description === elem.title
                              );
                              return (
                                statusData?.total_orders_this_week ??
                                elem.value ??
                                0
                              );
                            }
                            return elem.value ?? 0;
                          })()}
                        </h3>
                        <div
                          className={`tile-trend ${
                            (ordersByDate.data?.data?.find(
                              (item) => item.status_description === elem.title
                            )?.percentage_increase ?? 0) >= 0
                              ? "positive"
                              : "negative"
                          }`}
                        >
                          {(() => {
                            const statusData = ordersByDate.data?.data?.find(
                              (item) => item.status_description === elem.title
                            );
                            const percentageIncrease =
                              statusData?.percentage_increase ?? 0;

                            return percentageIncrease >= 0 ? (
                              <>
                                <ArrowUpOutlined
                                  style={{ marginRight: "4px" }}
                                />{" "}
                                {Math.abs(percentageIncrease).toFixed(2)}%
                              </>
                            ) : (
                              <>
                                <ArrowDownOutlined
                                  style={{ marginRight: "4px" }}
                                />{" "}
                                {Math.abs(percentageIncrease).toFixed(2)}%
                              </>
                            );
                          })()}
                          <span className="ms-1 text-muted">vs last 7days</span>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8 mb-4">
          <div className="card analytics-card">
            <div className="card-header pb-3 pt-3">
              <div className="row g-3 align-items-center">
                <div className="col-lg-4 col-md-12">
                  <h5>Today's Bookings</h5>
                </div>
                <div className="col-lg-5 col-md-8 col-sm-8">
                  <div className="search-control">
                    <SearchOutlined className="search-icon" />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by Order Id, Outlet or Customer"
                      value={sortFilter?.search || ""}
                      onChange={(e) =>
                        setSortFilter({ ...sortFilter, search: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="col-lg-3 col-md-4 col-sm-4">
                  <select
                    className="form-select filter-select"
                    value={sortFilter?.status || ""}
                    onChange={(e) =>
                      setSortFilter({ ...sortFilter, status: e.target.value })
                    }
                  >
                    <option value="">All Bookings</option>
                    {statusList?.data?.data.map((elem, i) => (
                      <option value={elem.description} key={i}>
                        {elem.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="dashboard-table">
                {todaysOrders.isLoading && (
                  <div className="text-center py-5">
                    <Skeleton
                      type={"rounded"}
                      height="200px"
                      width="100%"
                      backgroundColor="rgb(255 255 255 / 20%)"
                    />
                  </div>
                )}
                {todaysOrders.isSuccess && (
                  <Table
                    columns={columns}
                    dataSource={dataSource}
                    pagination={{
                      pageSize: 5,
                      position: ["bottomCenter"],
                      showSizeChanger: false,
                      showQuickJumper: false,
                      size: "small",
                    }}
                    scroll={{ x: 1200 }}
                    size="middle"
                    locale={{
                      emptyText: "No bookings found for today",
                    }}
                  />
                )}
              </div>
              <div className="row mt-6 m">
                <div className="col-12 text-center">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowBookNowModal(true);
                    }}
                  >
                    Create Booking
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 mb-4">
          <div className="card analytics-card h-100">
            <div className="card-header">
              <h5>Overview Distribution</h5>
            </div>
            <div className="card-body d-flex justify-content-center align-items-center">
              {(tilesData.isLoading || ordersByDate.isLoading) &&
                [1, 2, 3, 4, 5, 6, 7].map((elem, i) => (
                  <div className="tile-wrapper" key={i}>
                    <Skeleton
                      type={"rounded"}
                      height="165px"
                      width="100%"
                      backgroundColor="rgb(255 255 255 / 20%)"
                      className="mb-0"
                    />
                  </div>
                ))}
              {tilesData.isSuccess && (
                <div className="chart-container">
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showBookNowModal && (
        <BookNowModal onClose={() => setShowBookNowModal(false)} />
      )}
    </div>
  );
};

export default Dashboard;
